import { Bell, CaretRight, List } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui-kit/button';
import { Menubar, MenubarMenu, MenubarTrigger } from '@/components/ui-kit/menubar';
import { useSidebar } from '@/components/ui-kit/sidebar';
import { Notification } from '@/components/core/notification/component/notification/notification';
import { ProfileMenu } from '@/components/core/profile-menu/profile-menu';
import { LanguageSelector } from '@/components/core/language-selector/language-selector';
import { ThemeSwitcher } from '@/components/core/theme-switcher/theme-switcher';
import { cn } from '@/lib/utils';
import { getHrmShellBreadcrumbs, type HrmBreadcrumb } from './hrm-shell-breadcrumbs';

type NotificationsData = {
  notifications: unknown[];
  unReadNotificationsCount: number;
  totalNotificationsCount: number;
};

type HrmTopBarProps = {
  notifications: NotificationsData;
};

const HrmBreadcrumbs = ({ crumbs }: { crumbs: HrmBreadcrumb[] }) => {
  if (crumbs.length === 0) {
    return null;
  }

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex min-w-0 items-center gap-2 text-[13px] font-medium leading-[18px] text-neutral-600"
    >
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1;

        return (
          // On a phone only the current page is shown.
          <span
            key={`${crumb.label}-${index}`}
            className={cn('flex min-w-0 items-center gap-2', !isLast && 'max-md:hidden')}
          >
            {index > 0 ? (
              <CaretRight
                className={cn('size-3 shrink-0 text-neutral-400', isLast && 'max-md:hidden')}
                aria-hidden
              />
            ) : null}
            {crumb.href && !isLast ? (
              <Link to={crumb.href} className="hover:text-neutral-900">
                {crumb.label}
              </Link>
            ) : (
              <span className={cn(isLast && 'truncate text-neutral-900')}>{crumb.label}</span>
            )}
          </span>
        );
      })}
    </nav>
  );
};

export const HrmTopBar = ({ notifications }: HrmTopBarProps) => {
  const { isMobile, toggleSidebar } = useSidebar();

  const breadcrumbItems = getHrmShellBreadcrumbs();

  return (
    <header
      data-testid="hrm-top-bar"
      className="z-20 flex h-14 shrink-0 items-center gap-2 border-b border-neutral-200 bg-white px-4 sm:gap-3 sm:px-6"
    >
      {/* Below 1024px the sidebar is a drawer this button opens. */}
      {isMobile ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-md text-neutral-600 hover:bg-neutral-100"
          onClick={toggleSidebar}
          aria-label="Open menu"
        >
          <List className="size-5" />
        </Button>
      ) : null}

      <HrmBreadcrumbs crumbs={breadcrumbItems} />

      <div className="flex-1" />

      <ThemeSwitcher />

      <LanguageSelector />

      <Menubar className="border-none p-0">
        <MenubarMenu>
          <MenubarTrigger
            asChild
            className="cursor-pointer p-0 focus:bg-transparent data-[state=open]:bg-transparent"
          >
            <div className="relative">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-md text-neutral-600 hover:bg-neutral-100"
                aria-label="Notifications"
              >
                <Bell className="size-[18px]" />
              </Button>
              {notifications.unReadNotificationsCount > 0 ? (
                <span className="absolute right-2 top-2 size-2 rounded-full bg-error" />
              ) : null}
            </div>
          </MenubarTrigger>
          <Notification />
        </MenubarMenu>
      </Menubar>

      <ProfileMenu variant="compact" />
    </header>
  );
};
